import { useIsFetching } from "@tanstack/react-query"
import { itemIsChild, parentOfItem } from "../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../types/schema"

type IOrder = IRecord<
  "orders",
  "delivery_method" | "delivery" | "delivery_note_email" | "dpd_delivery_instructions"
> & {
  contact: IRecord<"contacts", "email">
  customer: IRecord<"customers"> & {
    contacts: IRecord<"contacts", "email" | "default_delivery_note">[]
  }
  items: IRecord<"order_items", "quantity">[]
  address: IRecord<
    "addresses",
    | "label"
    | "name"
    | "line_1"
    | "line_2"
    | "line_3"
    | "line_4"
    | "city"
    | "county"
    | "country"
    | "postcode"
    | "phone"
    | "email"
    | "contact"
  > | null
}

export async function deliverItems(
  skeem: typeof import("./skeem").skeem,
  currentUser: { id: string; branch: { id: string } },
  orderId: string,
  items: { id: string; quantity: number }[],
  invoice: boolean,
  onWillCreateBackOrder?: (data: {
    contact?: IRecord<"contacts", "email">
    deliveryNoteEmail: string
  }) => Promise<"onExport" | "onPick" | "onDelivery" | undefined>,
): Promise<
  [
    { id: string },
    {
      id: string
      identifier: string
      autoEmailOrderAcknowledgementOn?: "onExport" | "onPick" | "onDelivery"
    }?,
  ]
> {
  const [order] = await skeem.fetch<IOrder>({
    orders: {
      attributes: [
        "delivery",
        "delivery_method",
        "delivery_note_email",
        "dpd_delivery_instructions",
        {
          name: "customer",
          attributes: [
            {
              name: "contacts",
              attributes: ["email"],
              filter: { matches: { default_delivery_note: true } },
            },
          ],
        },
        { name: "contact", attributes: ["email"] },
        {
          name: "items",
          attributes: ["quantity"],
        },
        {
          name: "address",
          attributes: [
            "label",
            "name",
            "line_1",
            "line_2",
            "line_3",
            "line_4",
            "city",
            "county",
            "country",
            "postcode",
            "phone",
            "email",
            "contact",
          ],
        },
      ],
      filter: { matches: { id: orderId } },
    },
  })

  if (order.delivery) {
    throw new Error(`Order has already been marked as ${invoice ? "collected" : "delivered"}`)
  }

  const itemsToMove = []

  for (const item of order.items) {
    const quantity = Math.max(items.find(({ id }) => item.id === id)?.quantity || 0, 0)
    if (quantity < (item.quantity || 0)) {
      itemsToMove.push({ id: item.id, quantityToMove: (item.quantity ?? 0) - quantity })
    }
  }

  const willCreateBackOrder = itemsToMove.length > 0

  let backOrderMutationData: any
  // Build the back order data before any mutations and check the data is valid
  let onWillCreateBackOrderResult: string | undefined = undefined
  if (willCreateBackOrder) {
    const defaultContact = order.contact || order.customer.contacts[0]
    const deliveryNoteEmail = order.delivery_note_email || ""
    if (onWillCreateBackOrder) {
      onWillCreateBackOrderResult = await onWillCreateBackOrder({
        contact: defaultContact,
        deliveryNoteEmail,
      })
    }
  }

  const [delivery] = await skeem.mutate<{ invoice: boolean }>({
    deliveries: [
      {
        create: {
          invoice,
          delivery_service: order.delivery_method.toLowerCase().includes("dpd")
            ? "dpd"
            : order.delivery_method.toLowerCase().includes("fedex")
            ? "fedex"
            : "",
          dpd_delivery_instructions: order.dpd_delivery_instructions,
          branch: { set: currentUser.branch.id },
          user: { set: currentUser.id || null },
          order: { set: order.id },
          address: order.address
            ? {
                create: {
                  label: order.address.label,
                  name: order.address.name,
                  line_1: order.address.line_1,
                  line_2: order.address.line_2,
                  line_3: order.address.line_3,
                  line_4: order.address.line_4,
                  city: order.address.city,
                  county: order.address.county,
                  country: order.address.country,
                  postcode: order.address.postcode,
                  phone: stripPhoneNumber(order.address.phone),
                  email: order.address.email,
                  contact: order.address.contact,
                  branch: { set: currentUser.branch.id },
                  delivery_address: true,
                },
              }
            : undefined,
        },
      },
    ],
  })

  if (itemsToMove.length === 0) {
    await skeem.mutate({ orders: { update: { id: order.id, archived: true } } })
    return [delivery]
  }

  let newOrder:
    | {
        backOrderId: string
        id: string
        identifier: string
      }
    | undefined = undefined
  try {
    newOrder = await window.api.createBackOrder({
      fromOrderId: orderId,
      items: itemsToMove,
      autoEmailOrderAcknowledgementOn: onWillCreateBackOrderResult,
    })
  } catch (e) {
    // If we fail to create the back order, we need to roll back the delivery created
    // otherwise we end up with an incorrectly delivered order
    await skeem.mutate({ deliveries: { destroy: delivery.id } })
    console.error(e)
    return Promise.reject(e)
  }

  try {
    await handlePurchaseOrderBackOrder(skeem, order.id, newOrder.id)
  } catch (e) {
    console.error("Error handling purchase order back order", e)
  }

  return [delivery, newOrder]
}

// Strip phone number of spaces and if begins with 0044, change to 0 to make valid for DPD
export function stripPhoneNumber(phone: string) {
  return phone.replace(/\s/g, "").replace(/^0044/, "0")
}

async function handlePurchaseOrderBackOrder(
  skeem: typeof import("./skeem").skeem,
  parentOrderId: string,
  backOrderId: string,
) {
  // CHECK if parentOrder has a PO through OR
  // IF it does, check which items are on the backOrder that match the items through OR to PO
  // Take those items and create a new PO that relates to them only and remove them from the original PO

  type IParentOrder = IRecord<"orders"> & {
    orderRequest?: IRecord<"orderRequests"> & {
      items: IRecord<"orderRequestItems", "target_product" | "source_product">[]
      purchase_order: IRecord<
        "purchase_orders",
        | "carriage_pennies"
        | "email_on_export_details"
        | "hide_tax_on_documents"
        | "name"
        | "supplierDocumentNumber"
        | "supplier"
        | "preferred_warehouse"
        | "order_requested_date"
        | "user"
        | "branch"
        | "source_branch_id"
        | "sourceOrder"
        | "rootOrderRequest"
      > & {
        address: IRecord<
          "addresses",
          | "city"
          | "contact"
          | "client"
          | "country"
          | "email"
          | "line_1"
          | "line_2"
          | "line_3"
          | "line_4"
          | "county"
          | "name"
          | "phone"
          | "postcode"
          | "customer"
          | "customer_name"
          | "branch"
          | "delivery_address"
          | "label"
          | "lat"
          | "lng"
          | "showInCustomerAddressOptions"
          | "source_branch_id"
        >
        purchase_order_items: IRecord<
          "purchase_order_items",
          | "product"
          | "quantity"
          | "body"
          | "discountPercent"
          | "price_pennies"
          | "product_body"
          | "product_location"
          | "stock_code"
          | "weight"
        >[]
      }
    }
  }
  const parentOrder = await skeem.fetch2<IParentOrder>({
    first: {
      name: "orders",
      matches: { id: parentOrderId },
      attributes: [
        {
          name: "orderRequest",
          attributes: [
            { name: "items", attributes: ["source_product", "target_product"] },
            {
              name: "purchase_order",
              attributes: [
                {
                  name: "address",
                  attributes: [
                    "city",
                    "contact",
                    "client",
                    "country",
                    "email",
                    "line_1",
                    "line_2",
                    "line_3",
                    "line_4",
                    "county",
                    "name",
                    "phone",
                    "postcode",
                    "customer",
                    "customer_name",
                    "branch",
                    "delivery_address",
                    "label",
                    "lat",
                    "lng",
                    "showInCustomerAddressOptions",
                    "source_branch_id",
                  ],
                },
                "user",
                "carriage_pennies",
                "email_on_export_details",
                "hide_tax_on_documents",
                "name",
                "supplierDocumentNumber",
                "supplier",
                "preferred_warehouse",
                "order_requested_date",
                "branch",
                "source_branch_id",
                "rootOrderRequest",
                {
                  name: "purchase_order_items",
                  attributes: [
                    "product",
                    "quantity",
                    "body",
                    "discountPercent",
                    "price_pennies",
                    "product_body",
                    "product_location",
                    "stock_code",
                    "weight",
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  })

  if (!parentOrder.orderRequest?.purchase_order) {
    return
  }

  type IOrder = IRecord<"orders"> & {
    items: IRecord<"order_items", "product" | "quantity" | "type" | "stock_code" | "body">[]
  }
  const backOrder = await skeem.fetch2<IOrder>({
    first: {
      name: "orders",
      matches: { id: backOrderId },
      attributes: [
        {
          name: "items",
          attributes: [
            "quantity",
            "type",
            "stock_code",
            "body",
            {
              name: "product",
            },
          ],
        },
      ],
    },
  })

  // for each of the back order items, find the related OR item and PO item
  const mappedItems = backOrder.items
    .map(item => {
      // text items
      if (item.type == 1) {
        return null
      }
      const orItem = parentOrder.orderRequest?.items.find(
        ori => ori.target_product?.id === item.product?.id,
      )
      const poItem = orItem
        ? parentOrder.orderRequest?.purchase_order.purchase_order_items.find(
            poi => poi.product?.id === orItem.source_product?.id,
          )
        : undefined

      return {
        orderItem: item,
        orItem,
        poItem,
      }
    })
    .filter(Boolean)

  const isMissingProductMatches = mappedItems.filter(m => !m.orItem || !m.poItem)
  if (isMissingProductMatches.length > 0) {
    console.warn(
      "Some items on the back order could not be matched to the purchase order, so no split will be done",
      isMissingProductMatches,
    )
    return
  }

  const po = parentOrder.orderRequest.purchase_order
  const rootOrderRequestId = po.rootOrderRequest?.id ?? parentOrder.orderRequest?.id

  const updateItems: { id: string; quantity: number }[] = [] // items that will be updated on the OLD PO

  const movedItems: { id: string }[] = [] // Items that will be moved to the new PO
  const createItems: typeof po.purchase_order_items = [] // Items that will be created on the new PO - the remained of the updated items

  for (const item of mappedItems) {
    if (!item.poItem?.id) {
      throw new Error(`No PO item matched for item, cannot split (${item.orderItem.stock_code})`)
    }
    const quantityToMove = Math.max(item.orderItem.quantity || 0)
    if (quantityToMove <= 0) {
      continue
    }
    if (quantityToMove === item.poItem?.quantity) {
      // move whole item
      if (item.poItem?.id) {
        movedItems.push({ id: item.poItem.id })
      }
    } else {
      // split item

      updateItems.push({
        id: item.poItem.id,
        quantity: (item.poItem.quantity || 0) - quantityToMove,
      })
      createItems.push({ ...item.poItem, quantity: quantityToMove })
    }
  }
  po.purchase_order_items.forEach(poItem => {
    if (!poItem.product && poItem.body) {
      // text item - copy across
      createItems.push(poItem)
    }
  })

  const poAddress = po.address
  const duplicatedAddress = {
    city: poAddress.city,
    contact: poAddress.contact,
    client: { set: poAddress.client?.id ?? null },
    country: poAddress.country,
    email: poAddress.email,
    line_1: poAddress.line_1,
    line_2: poAddress.line_2,
    line_3: poAddress.line_3,
    line_4: poAddress.line_4,
    county: poAddress.county,
    name: poAddress.name,
    phone: poAddress.phone,
    postcode: poAddress.postcode,
    customer: { set: poAddress.customer?.id ?? null },
    customer_name: poAddress.customer_name,
    branch: { set: poAddress.branch?.id ?? null },
    delivery_address: poAddress.delivery_address,
    label: poAddress.label,
    lat: poAddress.lat,
    lng: poAddress.lng,
    showInCustomerAddressOptions: poAddress.showInCustomerAddressOptions,
    source_branch_id: poAddress.source_branch_id,
  }
  const newPurchaseOrderData = {
    // Address
    address: { create: duplicatedAddress },
    // PO attrs
    carriage_pennies: po.carriage_pennies,
    email_on_export_details: po.email_on_export_details,
    hide_tax_on_documents: po.hide_tax_on_documents,
    name: po.name,
    supplierDocumentNumber: po.supplierDocumentNumber,
    supplier: { set: po.supplier?.id ?? null },
    user: { set: po.user?.id ?? null },
    preferred_warehouse: { set: po.preferred_warehouse?.id ?? null },
    order_requested_date: po.order_requested_date,
    branch: { set: po.branch?.id ?? null },
    source_branch_id: po.source_branch_id,
    parent_purchase_order: { set: po.id },
    identifier: "PLACEHOLDER-IDENTIFIER",
    rootOrderRequest: rootOrderRequestId ? { set: rootOrderRequestId } : undefined,
  }

  const newPurchaseOrder = await skeem.mutate({
    purchase_orders: { create: newPurchaseOrderData },
  })

  try {
    await skeem.mutate({
      purchase_order_items: [
        // NEW items for the NEW PO
        ...createItems.map(item => ({
          create: {
            product: { set: item.product?.id ?? null },
            quantity: item.quantity,
            body: item.body,
            discountPercent: item.discountPercent,
            price_pennies: item.price_pennies,
            product_body: item.product_body,
            product_location: { set: item.product_location?.id ?? null },
            stock_code: item.stock_code,
            weight: item.weight,
            purchase_order: { set: newPurchaseOrder.id },
          },
        })),
        // ITEMS on the original PO that need quantities updating
        ...updateItems.map(item => ({
          update: {
            id: item.id,
            quantity: item.quantity,
          },
        })),
        // ITEMS moved from the OLD PO to the NEW PO (as they were fully moved)
        ...movedItems.map(item => ({
          update: {
            id: item.id,
            purchase_order: { set: newPurchaseOrder.id },
          },
        })),
      ],
    })
    // generate a proper identifier for the new PO now we know it has been created successfully
    const identifier = await nextPurchaseOrderIdentifier(skeem, po.source_branch_id)
    await skeem.mutate({ purchase_orders: { update: { id: newPurchaseOrder.id, identifier } } })
  } catch (e) {
    console.error("Error in handlePurchaseOrderBackOrder", e)
    // Handle rollback for creating the PO if items fail to update
    if (newPurchaseOrder) {
      try {
        await skeem.mutate({ purchase_orders: { destroy: newPurchaseOrder.id } })
      } catch (ee) {
        console.error("Error deleting new PO after failure in handlePurchaseOrderBackOrder", ee)
      }
    }
    // re-raise the original error
    throw e
  }
}

async function nextPurchaseOrderIdentifier(
  skeem: typeof import("./skeem").skeem,
  branchId: string,
) {
  const { nextPurchaseOrderIdentifier } = await skeem.mutate<{
    nextPurchaseOrderIdentifier: number
  }>({
    branches: { update: { id: branchId, nextPurchaseOrderIdentifier: { inc: 1 } } },
  })
  return `P${String(nextPurchaseOrderIdentifier).padStart(6, "0")}`
}

// window.handlePurchaseOrderBackOrder = handlePurchaseOrderBackOrder
